/**
 * src/pages/Embudo.jsx
 * Embudo comercial: visitas landing → clics CTA → diagnósticos F1 → registros
 * → contrataciones, con tasas de conversión entre etapas, más consultas de
 * contacto y las listas operativas de diagnósticos/leads recientes.
 * (Analytics fase A2 — ver PLAN_ANALYTICS_EMBUDO.md)
 *
 * En tres pestañas desde el 05-09. Manuel: «el módulo de EMBUDO tiene demasiada
 * información importante escondida bajo el scroll». Medido a 1600×900 antes de
 * tocar nada: **2.228 px de página sobre 810 visibles —2,75 pantallas—** y las
 * tres tablas de contactos empezaban en y=1.714, o sea **904 px por debajo del
 * pliegue**: había que bajar más de una pantalla entera para llegar a ellas.
 *
 * El corte no es «gráficos arriba, tablas abajo». Son tres preguntas:
 *   · Conversión — ¿cómo va el embudo?
 *   · Recorridos — ¿qué hace la gente en la landing? (ANÓNIMO)
 *   · Contactos  — ¿quién ha dejado sus datos? (IDENTIFICADO)
 *
 * ⚠️ La frontera entre las dos últimas es legal, no estética: el hash de
 * visitante rota a diario y no es reversible (Ley 21.719), y la identidad solo
 * aparece cuando la persona entrega sus datos. Esa distinción ya estaba escrita
 * en el subtítulo de la tarjeta de recorridos; meter las cuatro tablas en una
 * sola pestaña «Datos» la habría borrado.
 *
 * ⚠️⚠️ Y los contadores de las etiquetas no son adorno. **Una pestaña esconde su
 * contenido igual que un plegado**: si llega algo y nadie mira, es un fallo mudo
 * (§5). Con el número a la vista se sabe si hay algo sin pulsar — y `_cuenta`
 * devuelve `null`, no 0, mientras no haya lectura (§3): un «Contactos · 0»
 * mientras carga sería una afirmación sobre el negocio que nadie ha medido.
 */
import { Fragment, useState } from 'react'
import { Header } from '../components/layout/Header'
import { Card, KpiCard } from '../components/ui/Card'
import { BarraPestanas } from '../components/ui/Pestanas'
import { Pista } from '../components/ui/Pista'
import { Badge } from '../components/ui/Badge'
import { Table } from '../components/ui/Table'
import { useEmbudo } from '../hooks/useAdminPanel'
import { fechaHoraCL } from '../lib/horaChile'

const RANGOS = [
  { dias: 7, label: 'Semana' },
  { dias: 30, label: 'Mes' },
  { dias: 365, label: 'Año' },
]

const ETAPAS = [
  { key: 'visitantes', label: 'Visitantes landing', color: 'var(--color-running)' },
  { key: 'clics_cta', label: 'Clics en CTA', color: 'var(--color-running)' },
  { key: 'diagnosticos', label: 'Diagnósticos solicitados', color: 'var(--color-warn)' },
  { key: 'registros', label: 'Registros (trial)', color: 'var(--color-accent)' },
  { key: 'contrataciones', label: 'Contrataciones', color: 'var(--color-ok)' },
]

function fmtFecha(iso) {
  return fechaHoraCL(iso, {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  }) || '—'
}

function pct(parte, total) {
  if (!total) return null
  return Math.round((parte / total) * 1000) / 10
}

/**
 * Cuántas filas hay en juego, para la etiqueta de la pestaña.
 *
 * Devuelve `null` —no 0— si alguna lista no ha llegado. Un 0 en la pestaña dice
 * «no hay nadie»; un `null` se pinta como nada y no afirma. La diferencia es el
 * §3, y aquí importa el doble porque la etiqueta es lo ÚNICO que se ve de una
 * pestaña cerrada.
 */
export function _cuenta(...listas) {
  if (listas.some(l => !Array.isArray(l))) return null
  return listas.reduce((a, l) => a + l.length, 0)
}

const _rotulo = (texto, n) => (n == null ? texto : `${texto} · ${n}`)

// Cómo se llama en pantalla cada estado de suscripción. En castellano y en
// términos de negocio: `past_due` no le dice nada a nadie.
const _NOMBRE_ESTADO = {
  trialing: 'en prueba gratuita',
  past_due: 'impagas',
  canceled: 'de baja',
  pending:  'sin completar el pago',
  inactive: 'inactivas',
  unknown:  'en estado desconocido',
  sin_estado: 'sin estado',
}

/**
 * Qué MÁS pasó en la ventana, aparte de las contrataciones.
 *
 * ⚠️ Contexto de por qué existe: hasta el 06-09 `contrataciones` sumaba `active`
 * + `trialing`, así que la última barra del embudo —y el titular CONVERSIÓN
 * GLOBAL— contaba las pruebas gratuitas de 14 días como contratos. Manuel, con
 * el desglose delante: «que cuente solo lo que paga». Hecho.
 *
 * Pero lo que sale de la barra no puede desaparecer de la pantalla: una prueba
 * viva es un cliente potencial, no un cero. Así que los demás estados se
 * enseñan aquí, con su nombre, debajo de la etapa.
 *
 * Devuelve `null` si el desglose no ha llegado: sin él no se inventa nada (§3).
 */
export function otrosEstados(porEstado) {
  if (!porEstado || typeof porEstado !== 'object') return null
  const otros = Object.entries(porEstado)
    .filter(([e]) => e !== 'active')
    .filter(([, n]) => n > 0)
    .sort((a, b) => b[1] - a[1])
  if (!otros.length) return null
  return { otros, n: otros.reduce((a, [, x]) => a + x, 0) }
}

// El embudo de la prueba gratuita (Manuel, 06-09). Cuatro cifras que hasta la
// migración 038 eran IMPOSIBLES de medir: la fila de suscripción se sobrescribía
// y un trial convertido quedaba indistinguible de quien pagó desde el día uno.
//
// ⚠️ «Vivas» no pertenece a la ventana: describe el presente. Una prueba que
// arrancó el mes pasado y sigue viva hoy es justo a quien hay que convencer, y
// esconderla por caer fuera del rango sería perder de vista el trabajo pendiente.
// Las otras tres sí son de la cohorte que arrancó dentro, o la tasa no
// significaría nada (conversiones de hace meses partidas entre las de este mes).
//
// ⚠️⚠️ Y la fecha desde la que se mide va SIEMPRE a la vista. No hay historia
// anterior, así que sin esa línea un «0 convertidas» se leería como un fracaso
// comercial cuando lo único cierto es que antes no se guardaba (§3).
function EmbudoDePruebas({ trials, dias }) {
  if (!trials) return null
  const { solicitadas, vivas, convertidas, perdidas, en_curso: enCurso } = trials
  const tasa = pct(convertidas, convertidas + perdidas)
  const filas = [
    { clave: 'solicitadas', n: solicitadas, texto: `arrancaron en los últimos ${dias} días`,
      color: 'var(--color-accent)' },
    { clave: 'convertidas', n: convertidas, texto: 'de ésas, pasaron a pagar',
      color: 'var(--color-ok)' },
    { clave: 'perdidas', n: perdidas, texto: 'de ésas, se acabaron sin pagar',
      color: 'var(--color-warn)' },
    { clave: 'en-curso', n: enCurso, texto: 'de ésas, todavía en marcha',
      color: 'var(--color-border-2)' },
  ]
  const max = Math.max(1, ...filas.map(f => f.n || 0))

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-3)',
                    marginBottom: 'var(--space-3)' }}>
        <span data-trials="vivas" style={{
          fontFamily: 'var(--font-mono)', fontSize: 'var(--text-lg)',
          color: 'var(--color-text)', fontVariantNumeric: 'tabular-nums' }}>
          {vivas}
        </span>
        <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-2)' }}>
          en prueba <b>ahora mismo</b>
        </span>
        <Pista etiqueta="pruebas vivas" contenido={
          'Cuenta las que están en prueba HOY, vengan de la ventana elegida o de '
          + 'antes. Es la única de las cifras de esta tarjeta que no mira al '
          + 'pasado: son las personas a las que todavía se puede convencer.'} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'auto minmax(0, 1fr) auto',
                    gap: 'var(--space-1) var(--space-3)', alignItems: 'center',
                    fontSize: 'var(--text-sm)' }}>
        {filas.map(f => (
          <Fragment key={f.clave}>
            <span data-trial={f.clave} style={{
              fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums',
              color: 'var(--color-text)', textAlign: 'right', minWidth: '3ch' }}>
              {f.n}
            </span>
            <span aria-hidden="true" style={{ height: '10px', background: 'var(--color-surface-2)',
                                              borderRadius: 'var(--radius-pill)', overflow: 'hidden' }}>
              <span style={{ display: 'block', height: '100%',
                             width: `${f.n ? Math.max(2, (f.n / max) * 100) : 0}%`,
                             background: f.color, borderRadius: 'var(--radius-pill)' }} />
            </span>
            <span style={{ color: 'var(--color-text-3)', fontSize: 'var(--text-xs)' }}>
              {f.texto}
            </span>
          </Fragment>
        ))}
      </div>

      <div style={{ marginTop: 'var(--space-3)', fontSize: 'var(--text-xs)',
                    color: 'var(--color-text-3)', lineHeight: 1.6 }}>
        {tasa != null ? (
          <span data-trials="tasa">
            De las que ya terminaron, <strong style={{ color: 'var(--color-text-2)' }}>{tasa}%</strong> acabó pagando.
          </span>
        ) : (
          <span data-trials="tasa">
            Ninguna prueba de esta ventana ha terminado todavía: aún no hay tasa que dar.
          </span>
        )}
        {' '}
        <span data-trials="desde">Medido desde el {trials.desde}</span>
        <Pista etiqueta="desde cuándo se mide" contenido={
          'Antes de esa fecha la suscripción de un tenant guardaba solo su estado '
          + 'actual, así que una prueba que convertía dejaba de constar como '
          + 'prueba. No hay historia anterior y no se puede reconstruir: un cero '
          + 'aquí de fechas viejas significaría «no lo guardábamos», no «no pasó».'} />
      </div>
    </div>
  )
}

function Funnel({ embudo, porEstado }) {
  // Sin datos NO se pintan barras a cero: un embudo plano es una afirmación
  // sobre el negocio, y aquí lo único cierto es que no se pudo leer.
  if (!embudo) {
    return (
      <div style={{
        fontFamily: 'var(--font-mono)', fontSize: 'var(--text-sm)',
        color: 'var(--color-warn)', padding: 'var(--space-5)', textAlign: 'center',
      }}>
        No se pudo leer el embudo. No es que esté vacío: es que no hay lectura.
      </div>
    )
  }
  const max = Math.max(1, ...ETAPAS.map(e => embudo[e.key] || 0))
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {ETAPAS.map((etapa, i) => {
        const valor = embudo[etapa.key] || 0
        const previa = i > 0 ? (embudo[ETAPAS[i - 1].key] || 0) : null
        const conv = i > 0 ? pct(valor, previa) : null
        const otros = etapa.key === 'contrataciones' ? otrosEstados(porEstado) : null
        return (
          <div key={etapa.key}>
            <div style={{
              display: 'flex', justifyContent: 'space-between', marginBottom: 'var(--space-1)',
              fontSize: 'var(--text-sm)',
            }}>
              <span style={{ color: 'var(--color-text-2)' }}>{etapa.label}</span>
              <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--color-text)' }}>
                <strong>{valor}</strong>
                {conv != null && (
                  <span style={{ color: 'var(--color-text-3)', marginLeft: 'var(--space-2)' }}>
                    {conv}% de la etapa anterior
                  </span>
                )}
              </span>
            </div>
            <div style={{
              height: '22px', background: 'var(--color-surface-2)',
              borderRadius: '5px', overflow: 'hidden',
            }}>
              <div style={{
                width: `${Math.max(valor / max * 100, valor > 0 ? 2 : 0)}%`,
                height: '100%', background: etapa.color, borderRadius: '5px',
                transition: 'width 0.4s ease',
              }} />
            </div>

            {otros && (
              <div data-otros="contrataciones" style={{
                marginTop: 'var(--space-1)', fontSize: 'var(--text-xs)',
                color: 'var(--color-text-3)', lineHeight: 1.6,
              }}>
                En la misma ventana, además: {otros.otros.map(([e, n], k) => (
                  <span key={e}>
                    {k > 0 && ' · '}
                    <strong style={{ color: 'var(--color-text-2)' }}>{n}</strong>{' '}
                    {_NOMBRE_ESTADO[e] || e}
                  </span>
                ))}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

// ── El estado de un diagnóstico F1, que no es un «resultado» ────────────────
//
// POR QUÉ ESTÁ AQUÍ Y NO EN `Badge.jsx` (29-09-2026, aviso del hilo *Cliente final
// y onboarding*). Hasta hoy esta columna hacía
// `RESULTADO_COLOR[v] || (v === 'pending' ? 'yellow' : 'gray')`, o sea que los dos
// estados de espera —`espera_portal`, que existe desde hace semanas, y `espera_api`,
// que llega con su rama— salían en GRIS, exactamente igual que un estado
// desconocido. Un informe que está esperando y se va a reintentar se leía igual que
// uno sin clasificar (§5).
//
// 🔑 `RESULTADO_COLOR` es la paleta genérica de resultados y la comparte `SLA.jsx`,
// que no habla de diagnósticos F1. Meter ahí este vocabulario lo llevaría a una
// pantalla que no lo entiende, y además `Badge.jsx` es compartido. Aquí el mapa
// SUSTITUYE al parche de `pending`, así que los sitios que dan color a un estado de
// F1 pasan de dos a UNO, no de dos a tres (§4).
//
// ⚠️ Los dos ámbares son el mismo color a propósito, y no es un descuido de la
// lección de la semana pasada. Aquella no decía «no repitas un color»: decía que
// **el color no puede ser lo único que distingue**. Las dos esperas significan lo
// mismo —aparcado, se reintentará—, así que comparten el ámbar; lo que las separa es
// el texto, que dice a quién se espera.
const ESTADO_DIAG = {
  ok:            { color: 'green',  texto: 'listo' },
  error:         { color: 'red',    texto: 'error' },
  running:       { color: 'blue',   texto: 'calculando' },
  pending:       { color: 'yellow', texto: 'en cola' },
  espera_api:    { color: 'yellow', texto: 'esperando a Mercado Público' },
  espera_portal: { color: 'yellow', texto: 'esperando catálogo del portal' },
}

// El contador de reintentos, tal como lo manda el endpoint: `{veces, tope}`.
//
// ⚠️ `tope` puede ser `null`, y entonces NO se inventa un denominador: sale «3ª vez»
// en vez de «3 de 8». Ese caso es real hoy —el tope de `espera_api` vive en la rama
// del otro hilo— y es el §3: un número que nadie ha medido no se escribe. Se lee con
// `!= null` y no con `||`, porque un tope de 0 sería un dato, no una ausencia.
function textoDeEspera(espera) {
  if (!espera || !espera.veces) return ''
  return espera.tope != null
    ? ` · ${espera.veces} de ${espera.tope}`
    : ` · ${espera.veces}ª vez`
}

const DIAG_COLUMNS = [
  { key: 'creado_en', label: 'Fecha', mono: true, nowrap: true, width: '120px', render: fmtFecha },
  {
    key: 'empresa', label: 'Empresa',
    render: (v, row) => (
      <div>
        <div style={{ fontWeight: 500 }}>{v || '(sin nombre)'}</div>
        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-3)', fontFamily: 'var(--font-mono)' }}>
          {row.email}
        </div>
      </div>
    ),
  },
  {
    // Más ancha que antes (95 px) porque ahora dice qué pasa en vez del estado en
    // crudo: «esperando a Mercado Público · 3 de 8» no cabe en 95 px.
    key: 'estado', label: 'Estado', width: '230px',
    render: (v, row) => {
      const e = ESTADO_DIAG[v]
      // Un estado que no conocemos sale en gris CON su nombre en crudo: es lo único
      // honesto que se puede decir de él, y así se ve que hace falta añadirlo aquí.
      return <Badge color={e ? e.color : 'gray'}>{(e ? e.texto : v) + textoDeEspera(row.espera)}</Badge>
    },
  },
  {
    key: 'enviado', label: 'Email', width: '80px', mono: true,
    render: v => v
      ? <span style={{ color: 'var(--color-ok)' }}>enviado</span>
      : <span style={{ color: 'var(--color-text-3)' }}>—</span>,
  },
]

const LEAD_COLUMNS = [
  { key: 'creado_en', label: 'Fecha', mono: true, nowrap: true, width: '120px', render: fmtFecha },
  {
    key: 'empresa', label: 'Empresa',
    render: (v, row) => (
      <div>
        <div style={{ fontWeight: 500 }}>{v || '(sin nombre)'}</div>
        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-3)', fontFamily: 'var(--font-mono)' }}>
          {row.email}
        </div>
      </div>
    ),
  },
  { key: 'origen', label: 'Origen', mono: true, width: '100px' },
]

const REGISTRO_COLUMNS = [
  { key: 'creado_en', label: 'Fecha', mono: true, nowrap: true, width: '120px', render: fmtFecha },
  {
    key: 'nombre', label: 'Usuario',
    render: (v, row) => (
      <div>
        <div style={{ fontWeight: 500 }}>{v || '(sin nombre)'}</div>
        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-3)', fontFamily: 'var(--font-mono)' }}>
          {row.email}
        </div>
      </div>
    ),
  },
  {
    key: 'estado_suscripcion', label: 'Suscripción', width: '120px',
    render: (v, row) => v
      ? <Badge color={v === 'active' ? 'green' : v === 'trialing' ? 'blue' : 'gray'} dot>
          {row.tier ? `${row.tier} · ` : ''}{v}
        </Badge>
      : <span style={{ color: 'var(--color-text-3)', fontSize: 'var(--text-sm)' }}>sin plan</span>,
  },
]

const VISITANTE_COLUMNS = [
  {
    key: 'primera_hora', label: 'Cuándo', mono: true, nowrap: true, width: '130px',
    render: v => fmtFecha(v),
  },
  {
    key: 'visitante', label: 'Visitante', mono: true, width: '110px',
    render: v => <span title="Código anónimo (rota a diario)">#{v}</span>,
  },
  {
    key: 'paginas', label: 'Páginas vistas', mono: true,
    render: v => v?.length ? v.join(' · ') : '—',
  },
  {
    key: 'ctas', label: 'CTAs clicados',
    render: v => v?.length
      ? v.map(c => <Badge key={c} color="blue">{c}</Badge>).reduce((a, b) => [a, ' ', b])
      : <span style={{ color: 'var(--color-text-3)', fontSize: 'var(--text-sm)' }}>ninguno</span>,
  },
  {
    key: 'utm_source', label: 'Fuente', mono: true, width: '110px',
    render: v => v || <span style={{ color: 'var(--color-text-3)' }}>directa</span>,
  },
]

const PESTANAS_EMBUDO = [
  { clave: 'conversion', label: 'Conversión' },
  { clave: 'recorridos', label: 'Recorridos' },
  { clave: 'contactos',  label: 'Contactos' },
]

/**
 * Las tres tablas de «Contactos», una por sub-pestaña y a ancho completo.
 *
 * 🔴 **POR QUÉ NO ESTÁN LAS TRES A LA VEZ (24-09-2026).** Estaban, en una rejilla
 * `repeat(auto-fit, minmax(340px, 1fr))` con `overflowX: auto` en cada tabla. Con
 * los datos de prueba de la suite eso parecía caber; **medido con correos y
 * nombres de largo REAL**, la tabla de registros necesita **574 px** y recibía:
 *
 *   · 377 px a 1440 → ocultas «Usuario» **y** «Suscripción»
 *   · 430 px a 1600 → oculta «Suscripción»
 *   · 537 px a 1920 → oculta «Suscripción» (se salía 37 px)
 *
 * O sea: en cualquier pantalla normal. Y no era un problema estético. La columna
 * que caía por el borde es **«Suscripción», el único sitio del panel donde se ve
 * el estado de suscripción de un alta**: Manuel la buscó siguiendo
 * `docs/RUNBOOK_TRIAL_Y_RESUMEN_SEMANAL.md`, no la encontró, y concluyó que no
 * existía. Es el §10 en su forma más cara — el dato estaba calculado, servido y
 * pintado, y su lector no lo veía.
 *
 * ⚠️ **Por qué sub-pestañas y no una rejilla más ancha.** Subir el `minmax` a 580
 * px arreglaba lo medido HOY y dejaba una cuenta atrás: el ancho que hace falta
 * depende del largo de los correos de los clientes, que cambia sin avisar y sin
 * romper nada visiblemente. Una tabla a ancho completo son ~1.160 px a 1440 —
 * margen de sobra sobre 574— y no depende de adivinar ese dato.
 *
 * ⚠️⚠️ **Y el peaje que esto cobra, pagado aquí mismo.** Una sub-pestaña esconde
 * su tabla igual que la escondía el scroll: cambiar una columna oculta por una
 * tabla oculta no sería un arreglo. Por eso cada sub-pestaña lleva su contador
 * —y `_cuenta` devuelve `null`, no 0, mientras no haya lectura (§3)—, que es lo
 * único que se ve de una tabla sin pulsar.
 *
 * 📌 Los `titulo` se mantienen palabra por palabra: `RUNBOOK_TRIAL_Y_RESUMEN_SEMANAL.md`
 * cita «Registros recientes» para mandar a alguien a esa tabla, y renombrarla
 * volvería mentira el documento en silencio (lo que vigila
 * `tests/test_rotulos_de_pantalla.py`). La etiqueta de la sub-pestaña es corta
 * para no repetir el título dos veces en la misma pantalla.
 */
const SUBPESTANAS_CONTACTOS = [
  {
    clave: 'diagnosticos', label: 'Diagnósticos', lista: 'diagnosticos_recientes',
    titulo: 'Diagnósticos recientes', subtitulo: 'Solicitudes del diagnóstico gratuito (F1)',
    columnas: DIAG_COLUMNS, vacio: 'Sin diagnósticos aún',
  },
  {
    clave: 'consultas', label: 'Consultas', lista: 'leads_recientes',
    titulo: 'Consultas recientes', subtitulo: 'Formulario de contacto de la landing',
    columnas: LEAD_COLUMNS, vacio: 'Sin consultas aún',
  },
  {
    clave: 'registros', label: 'Registros', lista: 'registros_recientes',
    titulo: 'Registros recientes', subtitulo: 'Cuentas creadas (identificados)',
    columnas: REGISTRO_COLUMNS, vacio: 'Sin registros aún',
  },
]

export default function Embudo() {
  const [dias, setDias] = useState(30)
  const [pestana, setPestana] = useState('conversion')
  // Cuál de las tres tablas de «Contactos» está abierta. Ver el comentario de
  // `SUBPESTANAS_CONTACTOS`: la de registros es la que llevaba la columna que no
  // se veía, pero la primera sigue siendo diagnósticos porque es el orden del
  // embudo (diagnóstico → consulta → registro) y no el de la avería.
  const [subContacto, setSubContacto] = useState(SUBPESTANAS_CONTACTOS[0].clave)
  const { data, isLoading } = useEmbudo(dias)
  // Sin `|| {}`, igual que SLA y Uso. `pct` ya devuelve null si no hay total,
  // así que la conversión global no se inventa un 0 % cuando no hay visitantes
  // medidos — que no es lo mismo que una landing sin visitas.
  const embudo = data?.embudo
  const tasaGlobal = pct(embudo?.contrataciones, embudo?.visitantes)

  const nRecorridos = _cuenta(data?.visitantes_recientes)
  const nContactos = _cuenta(data?.diagnosticos_recientes, data?.leads_recientes,
                             data?.registros_recientes)
  // El `??` no es adorno defensivo: sin él, una clave que dejara de existir
  // pintaría una pestaña «Contactos · 24» con el panel en blanco debajo, que es
  // un fallo mudo (§5) en vez de una tabla.
  const subActiva = SUBPESTANAS_CONTACTOS.find(s => s.clave === subContacto)
                    ?? SUBPESTANAS_CONTACTOS[0]
  const pestanas = [
    PESTANAS_EMBUDO[0],
    { ...PESTANAS_EMBUDO[1], label: _rotulo('Recorridos', nRecorridos) },
    { ...PESTANAS_EMBUDO[2], label: _rotulo('Contactos', nContactos) },
  ]

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Header
        title="Embudo"
        subtitle={`Conversión landing → contratación · captura desde ${data?.captura_desde || '2026-07-13'}`}
        action={
          /* El rango se queda ENCIMA de las pestañas a propósito: manda sobre
             las tres, y bajarlo a una de ellas haría creer que solo filtra esa. */
          <div style={{ display: 'flex', gap: 'var(--space-1)' }}>
            {RANGOS.map(r => (
              <button
                key={r.dias}
                onClick={() => setDias(r.dias)}
                style={{
                  border: dias === r.dias
                    ? '1px solid var(--color-accent)'
                    : '1px solid var(--color-border)',
                  background: dias === r.dias ? 'var(--color-accent-soft)' : 'var(--color-surface)',
                  color: dias === r.dias ? 'var(--color-accent)' : 'var(--color-text-2)',
                  borderRadius: 'var(--radius-sm)', padding: '5px 12px', cursor: 'pointer',
                  fontSize: 'var(--text-sm)', fontFamily: 'var(--font-mono)',
                }}
              >
                {r.label}
              </button>
            ))}
          </div>
        }
      />

      <BarraPestanas pestanas={pestanas} activa={pestana} onCambio={setPestana} />

      <div style={{ flex: 1, overflow: 'auto', padding: '24px 28px' }}>
        {pestana === 'conversion' && (<>
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '14px',
            marginBottom: 'var(--space-5)',
          }}>
            <KpiCard label="VISITANTES" valor={embudo?.visitantes} cargando={isLoading}
                     color="var(--color-running)"
                     sub={embudo?.pageviews != null
                       ? `${embudo.pageviews.toLocaleString('es-CL')} páginas vistas · únicos/día`
                       : 'únicos/día'}
                     nada="nadie entró a la landing" />
            <KpiCard label="DIAGNÓSTICOS" valor={embudo?.diagnosticos} cargando={isLoading}
                     color="var(--color-warn)" sub={`últimos ${dias} días`}
                     nada={`ningún diagnóstico en ${dias} días`} />
            <KpiCard label="CONSULTAS CONTACTO" valor={data?.consultas} cargando={isLoading}
                     sub="formulario de la landing"
                     nada="ninguna consulta recibida" />
            <KpiCard label="CONVERSIÓN GLOBAL" valor={tasaGlobal} cargando={isLoading} sufijo="%"
                     color={tasaGlobal ? 'var(--color-ok)' : 'var(--color-text-3)'}
                     sub="visitante → contratación"
                     pista={<>
                       Cuenta <b>solo lo que paga</b>: suscripciones en estado activo. Las
                       pruebas gratuitas de 14 días <b>no</b> entran (decisión de Manuel,
                       06-09) — hasta esa fecha sí lo hacían, así que una tasa anterior a
                       ese día no es comparable con ésta. Lo que hay en prueba o impagado
                       se lee bajo la última barra del embudo.
                     </>} />
          </div>

          <Card
            title="Embudo de conversión"
            subtitle={`Últimos ${dias} días · los datos de visitas acumulan desde el ${data?.captura_desde || '2026-07-13'}`}
          >
            {isLoading ? (
              <div style={{
                textAlign: 'center', padding: 'var(--space-6)', color: 'var(--color-text-3)',
                fontSize: 'var(--text-sm)', fontFamily: 'var(--font-mono)',
              }}>
                Cargando…
              </div>
            ) : (
              <Funnel embudo={embudo} porEstado={data?.suscripciones_por_estado} />
            )}
          </Card>

          {/* Debajo del embudo y no en otra pestaña: es la continuación natural
              de su última etapa — quién está a un paso de pagar y quién se cayó
              justo ahí. */}
          <Card
            title="La prueba gratuita"
            subtitle="Cuántas se piden, cuántas siguen vivas, cuántas acaban pagando y cuántas se pierden"
            style={{ marginTop: 'var(--space-5)' }}
          >
            {isLoading
              ? <div style={{ color: 'var(--color-text-3)', fontFamily: 'var(--font-mono)',
                              fontSize: 'var(--text-sm)' }}>Cargando…</div>
              : <EmbudoDePruebas trials={data?.trials} dias={dias} />}
          </Card>
        </>)}

        {pestana === 'recorridos' && (
          <Card
            title="Recorridos de visitantes"
            subtitle="Conducta anónima por visitante y día: qué vio, qué clicó y de dónde vino. La identidad aparece cuando dejan sus datos (diagnóstico, contacto o registro) — por diseño no se rastrea a nadie sin su consentimiento (Ley 21.719)."
            padding="0"
          >
            <Table columns={VISITANTE_COLUMNS} data={data?.visitantes_recientes} loading={isLoading}
                   emptyMessage="Sin visitas en el rango" />
          </Card>
        )}

        {pestana === 'contactos' && (<>
          {/* La barra de sub-pestañas es la MISMA `BarraPestanas` de la cabecera y
              no una copia con otro aspecto: `frontend/src/components/ui/Pestanas.jsx`
              no es zona de este hilo (Regla C) ni está en la lista compartida, y
              duplicar el patrón aquí para que se viera «más pequeño» habría dejado
              dos barras de pestañas que se despegan a la primera (§4). */}
          <div style={{ marginBottom: 'var(--space-4)' }}>
            <BarraPestanas
              pestanas={SUBPESTANAS_CONTACTOS.map(s => ({
                clave: s.clave, label: _rotulo(s.label, _cuenta(data?.[s.lista])),
              }))}
              activa={subContacto}
              onCambio={setSubContacto}
            />
          </div>
          <Card title={subActiva.titulo} subtitle={subActiva.subtitulo} padding="0">
            <Table columns={subActiva.columnas} data={data?.[subActiva.lista]}
                   loading={isLoading} emptyMessage={subActiva.vacio} />
          </Card>
        </>)}
      </div>
    </div>
  )
}
